/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import * as React from "react";
import { ChoiceGroup, IChoiceGroupOption } from "@fluentui/react";

import { IFieldDefinitionPanelCheckboxProps } from ".";
import { FieldDefinitionPanelCheckboxCommands } from "./FieldDefinitionPanelCheckboxViewModel";
import { IFieldDefinitionPanelCheckboxState } from "./FieldDefinitionPanelCheckboxInterfaces";
import { panelFooterProps } from "./FieldDefinitionPanelStackTokens";
import { IPanelFooterProps } from "./IPanelFooterProps";
import FieldDefinitionPanel from "./FieldDefinitionPanel";

const fieldDefinitionPanelCheckboxCommands = new FieldDefinitionPanelCheckboxCommands();
const cmds = fieldDefinitionPanelCheckboxCommands.Cmds;

const options: IChoiceGroupOption[] = [
    { key: "false", text: "Unchecked" },
    { key: "true", text: "Checked" }
];

const FieldDefinitionPanelCheckbox = (props: IFieldDefinitionPanelCheckboxProps): JSX.Element => {
    const initialState: IFieldDefinitionPanelCheckboxState = {
        common: { ...props.common },
        data: { ...props.data },
        errors: {},
        touched: {},
        isValid: !props.common.isNew,
        isDirty: false
    };

    const [state, dispatch] = React.useReducer(
        fieldDefinitionPanelCheckboxCommands.Reducer,
        initialState
    );

    const onSaveFieldDefinition = () => {
        if (props.common.onSaveContent !== undefined) {
            const data = fieldDefinitionPanelCheckboxCommands.Save(state);
            props.common.onSaveContent(data);
        }
    };

    const onDefaultValueChange = (
        ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        selection?: IChoiceGroupOption
    ) => {
        if (selection !== undefined) {
            const checked = selection.key === "true";
            dispatch(cmds.onDefaultValueChanged(checked));
        }
    };

    const footerProps: IPanelFooterProps = {
        onSave: onSaveFieldDefinition,
        onCancel: props.common.onClosePanel,
        isNew: props.common.isNew,
        isDirty: state.isDirty,
        isValid: state.isValid,
        panelFooterProps
    };

    return (
        <FieldDefinitionPanel
            title={props.common.panelTitle}
            isOpen={props.common.isOpen}
            onClosePanel={props.common.onClosePanel}
            footerProps={footerProps}
            settingName={state.data.settingName}
            onChangeSettingName={(name, value) => dispatch(cmds.onTextChanged({ name, value }))}
            settingNameErrorMessage={state.touched.settingName ? state.errors.settingName : ""}
            onBlurSettingName={name => dispatch(cmds.onBlur(name))}
            settingNameReadOnly={!props.common.isNew}
            titleValue={state.data.title}
            onChangeTitle={(name, value) => dispatch(cmds.onTextChanged({ name, value }))}
            titleErrorMessage={state.touched.title ? state.errors.title : ""}
            onBlurTitle={name => dispatch(cmds.onBlur(name))}
            tooltipText={state.data.tooltip}
            helperText={state.data.helper}
            onHelpTextChange={changeData => {
                dispatch(cmds.onHelpTextChange(changeData));
            }}
            accessLevel={state.data.accessLevels}
            onAccessLevelChange={accessLevel => dispatch(cmds.onAccessLevelChanged(accessLevel))}
            auditChanges={state.data.auditChanges}
            onAuditChangesChanged={(checked: boolean) => dispatch(cmds.onAuditChanged(checked))}
            controlType={state.data.definitionType}
        >
            <ChoiceGroup
                label="Default value"
                selectedKey={state.data.defaultValue.toString()}
                options={options}
                onChange={onDefaultValueChange}
            />
        </FieldDefinitionPanel>
    );
};

const CheckboxPanel = FieldDefinitionPanelCheckbox;
export { CheckboxPanel };
